import { useMemo } from 'react';
import type { TypeCurvesListingFilters } from './use-type-curves-listing';

/**
 * Builds the Type Curve listing map's vector-tile template url(s).
 *
 * The listing map draws every item matching the current filters, not just the
 * visible page. The FE sends the same filter payload it posts to the listing
 * endpoint on the tile url; the tile resolves the full matching set server-side
 * and renders it (`Map::Tiles::TypeCurveAreas` / `TypeCurveScenarioAreaConfigs`).
 *   * type_curves / workspaces -> `/maps/tiles/type_curve_areas`      (+ view)
 *   * scenarios                -> `/maps/tiles/type_curve_scenario_area_configs`
 *
 * Filters travel as snake_case query params: the tile is a GET, and the app-wide
 * request-key transform only underscores POST bodies, so the keys are sent in
 * the shape the tile controller permits. Type Curves / Workspaces draw the Type
 * Curve AREA (which every TC has), not development-area units (almost none have).
 *
 * The returned value is a `tiles` array for a Mapbox vector source (each url
 * carries the {x}/{y}/{z} placeholders Mapbox fills per tile).
 */

export type TcMapView =
  'type_curves' | 'workspaces' | 'scenarios' | 'inventories';

const TILE_PATHS: Record<TcMapView, string> = {
  type_curves: '/maps/tiles/type_curve_areas',
  workspaces: '/maps/tiles/type_curve_areas',
  scenarios: '/maps/tiles/type_curve_scenario_area_configs',
  inventories: '/maps/tiles/type_curve_areas',
};

/**
 * The area tile serves both the Type Curves and Workspaces views (same path);
 * `view` tells the resolver which projection's matching areas to render. The
 * scenarios tile has a path of its own, so it needs no view param.
 */
const VIEW_PARAM: Partial<Record<TcMapView, string>> = {
  type_curves: 'type_curves',
  workspaces: 'workspaces',
  // Inventories shares the area tile. Note `view` alone changes nothing: with
  // no filter params the tile renders every active area identically for every
  // view, because all it selects is which query resolves the filters.
  inventories: 'inventories',
};

/**
 * The tile id (`x-y-z`) matches `Map::Tiles::*.find_by_param!`, which splits on
 * `-` in that order. Mapbox substitutes the {x}/{y}/{z} tokens per tile request.
 */
const TILE_COORD_TEMPLATE = '{x}-{y}-{z}';

/**
 * Serializes the camelCase listing filter payload into the snake_case query
 * params the tile controller permits. Only present values are emitted, so an
 * unset filter never reaches the tile.
 *
 * @param {TypeCurvesListingFilters} f - The active listing filters.
 * @return {string[]} Encoded `key=value` / `key[]=value` param strings.
 */
function serializeFilters(f: TypeCurvesListingFilters): string[] {
  const parts: string[] = [];
  const enc = (v: string | number | boolean) => encodeURIComponent(String(v));
  const scalar = (key: string, v: string | number | boolean) =>
    parts.push(`${key}=${enc(v)}`);
  const list = (key: string, vals: ReadonlyArray<string | number>) =>
    vals.forEach((v) => parts.push(`${key}[]=${enc(v)}`));

  if (f.text) scalar('text', f.text);
  if (typeof f.active === 'boolean') scalar('active', f.active);
  if (f.hasNewWells) scalar('has_new_wells', f.hasNewWells);
  if (f.developmentType) scalar('development_type', f.developmentType);
  if (typeof f.promoted === 'boolean') scalar('promoted', f.promoted);
  if (typeof f.tasksComplete === 'boolean')
    scalar('tasks_complete', f.tasksComplete);

  if (f.basin?.length) list('basin', f.basin);
  if (f.formation?.length) list('formation', f.formation);
  if (f.status?.length) list('status', f.status);
  if (f.typeCurveArea?.length) list('type_curve_area', f.typeCurveArea);
  if (f.dataSource?.length) list('data_source', f.dataSource);
  if (f.promotedBy?.length) list('promoted_by', f.promotedBy);
  if (f.createdBy?.length) list('created_by', f.createdBy);
  if (f.dcaScenario?.length) list('dca_scenario', f.dcaScenario);
  if (f.scalingVariables?.length) list('scaling_variables', f.scalingVariables);
  if (f.typeCurveScenarioIds?.length)
    list('type_curve_scenario_ids', f.typeCurveScenarioIds);

  // Inventories-only. The other views ignore unknown params, so these are safe
  // to serialize unconditionally rather than branching on the view.
  if (f.state?.length) list('state', f.state);
  if (f.inventoryPackageName?.length)
    list('inventory_package_name', f.inventoryPackageName);
  if (f.verification) scalar('verification', f.verification);
  if (typeof f.isDefault === 'boolean') scalar('is_default', f.isDefault);
  if (f.uniqueTcId) scalar('unique_tc_id', f.uniqueTcId);
  if (f.updatedBy?.length) list('updated_by', f.updatedBy);

  if (typeof f.basinAreaCount?.min === 'number')
    scalar('basin_area_count[min]', f.basinAreaCount.min);
  if (typeof f.basinAreaCount?.max === 'number')
    scalar('basin_area_count[max]', f.basinAreaCount.max);
  if (f.createdAt?.start) scalar('created_at[start]', f.createdAt.start);
  if (f.createdAt?.end) scalar('created_at[end]', f.createdAt.end);
  if (f.updatedAt?.start) scalar('updated_at[start]', f.updatedAt.start);
  if (f.updatedAt?.end) scalar('updated_at[end]', f.updatedAt.end);

  return parts;
}

/**
 * Builds the vector tile template for the active view + filters. Absolute
 * (origin-prefixed) so Mapbox resolves it regardless of the page url.
 *
 * @param {TcMapView} view - Active listing view.
 * @param {TypeCurvesListingFilters} filters - The active listing filters.
 * @return {string[]} A single-element tiles array.
 */
export function buildTcTileUrls(
  view: TcMapView,
  filters: TypeCurvesListingFilters
): string[] {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const params: string[] = [];
  const viewParam = VIEW_PARAM[view];
  if (viewParam) params.push(`view=${viewParam}`);
  params.push(...serializeFilters(filters));

  const query = params.length ? `?${params.join('&')}` : '';
  return [`${origin}${TILE_PATHS[view]}/${TILE_COORD_TEMPLATE}${query}`];
}

/**
 * Memoized tile urls for the active view + filters. Keyed on the serialized
 * view+filters so a new-but-equal filters object does not remint the source
 * (which would refetch tiles).
 *
 * @param {TcMapView} view - Active listing view.
 * @param {TypeCurvesListingFilters} filters - The active listing filters.
 * @return {string[]} The tiles array.
 */
export function useTcTileUrls(
  view: TcMapView,
  filters: TypeCurvesListingFilters
): string[] {
  const key = JSON.stringify([view, filters]);
  // `key` stands in for `filters` — a stable identity for the same filter set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => buildTcTileUrls(view, filters), [key]);
}
